import { useForm, getFormProps } from "@conform-to/react"
import { getZodConstraint, parseWithZod } from "@conform-to/zod"
import { useFetcher } from "react-router"
import { HoneypotInputs } from "remix-utils/honeypot/react"
import { z } from "zod"
import { AnalyticsEvents } from "#app/utils/analytics"
import { cn } from "#app/utils/misc.tsx"
import { useUmami } from "#app/utils/umami"
import { Icon } from "#app/components/ui/icon.tsx"
import { StatusButton } from "#app/components/ui/status-button.tsx"

const NewsletterFormSchema = z.object({
	email: z
		.string({ required_error: "Se requiere un correo electrónico" })
		.email("Correo electrónico no válido."),
	listType: z.enum(["newsletter", "blog"]).default("newsletter"),
})

type NewsletterFormData = {
	result: Record<string, unknown>
	status: "success" | "error"
	message?: string
}

export function NewsletterForm({
	listType = "newsletter",
	variant = "inline",
	className,
}: {
	listType?: "newsletter" | "blog"
	variant?: "inline" | "prominent"
	className?: string
}) {
	const fetcher = useFetcher<NewsletterFormData>()
	const { umami } = useUmami()

	const isSuccess = fetcher.data?.status === "success"
	const isError = fetcher.data?.status === "error"
	const fetcherStatus =
		fetcher.state === "submitting"
			? "pending"
			: isSuccess
				? "success"
				: isError
					? "error"
					: "idle"

	const [form, fields] = useForm({
		id: `newsletter-${listType}-${variant}`,
		constraint: getZodConstraint(NewsletterFormSchema),
		lastResult: fetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: NewsletterFormSchema })
		},
		shouldRevalidate: "onBlur",
		defaultValue: {
			listType,
		},
	})

	if (variant === "inline") {
		return (
			<div className={cn("w-full max-w-sm", className)}>
				<fetcher.Form
					method="POST"
					action="/resources/newsletter"
					{...getFormProps(form)}
					className="flex items-start gap-2"
					onSubmit={() => {
						umami().track(AnalyticsEvents.NEWSLETTER_SUBSCRIBE)
					}}
				>
					<HoneypotInputs />
					<input type="hidden" name="listType" value={listType} />

					<div className="flex-1">
						<input
							name={fields.email.name}
							type="email"
							placeholder="Tu correo electrónico"
							className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							aria-label="Correo electrónico"
							required
						/>
						{fields.email.errors && (
							<p className="mt-1 text-xs text-destructive">
								{fields.email.errors[0]}
							</p>
						)}
					</div>

					<StatusButton
						type="submit"
						size="sm"
						status={fetcherStatus}
						disabled={fetcher.state !== "idle"}
					>
						<Icon name="envelope-closed" />
						<span className="sr-only">Suscribirse</span>
					</StatusButton>
				</fetcher.Form>

				{isSuccess && (
					<p className="mt-1 text-xs text-green-600 dark:text-green-400">
						{fetcher.data?.message}
					</p>
				)}
			</div>
		)
	}

	return (
		<section
			className={cn(
				"rounded-xl border border-border/50 bg-secondary/50 px-6 py-10 text-center dark:bg-secondary/20",
				className,
			)}
		>
			<Icon
				name="envelope-closed"
				size="xl"
				className="mx-auto mb-4 text-primary dark:text-primary-foreground"
			/>
			<h3 className="font-serif text-2xl font-medium text-foreground">
				{listType === "blog"
					? "Suscríbete a nuestro blog"
					: "Suscríbete a nuestro newsletter"}
			</h3>
			<p className="mx-auto mt-2 max-w-md text-muted-foreground dark:text-foreground/70">
				{listType === "blog"
					? "Recibe nuestros nuevos artículos directamente en tu correo."
					: "Mantente al día con las últimas novedades y ofertas exclusivas."}
			</p>

			<fetcher.Form
				method="POST"
				action="/resources/newsletter"
				{...getFormProps(form)}
				className="mx-auto mt-6 flex max-w-md items-start gap-2"
				onSubmit={() => {
					umami().track(
						listType === "blog"
							? AnalyticsEvents.BLOG_NEWSLETTER_SUBSCRIBE
							: AnalyticsEvents.NEWSLETTER_SUBSCRIBE,
					)
				}}
			>
				<HoneypotInputs />
				<input type="hidden" name="listType" value={listType} />

				<div className="flex-1">
					<input
						name={fields.email.name}
						type="email"
						placeholder="Tu correo electrónico"
						className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						aria-label="Correo electrónico"
						required
					/>
					{fields.email.errors && (
						<p className="mt-1 text-left text-xs text-destructive">
							{fields.email.errors[0]}
						</p>
					)}
				</div>

				<StatusButton
					type="submit"
					status={fetcherStatus}
					disabled={fetcher.state !== "idle"}
				>
					Suscribirme
				</StatusButton>
			</fetcher.Form>

			{isSuccess && (
				<p className="mt-3 text-sm text-green-600 dark:text-green-400">
					{fetcher.data?.message}
				</p>
			)}
			{isError && fetcher.data?.message && (
				<p className="mt-3 text-sm text-destructive">
					{fetcher.data?.message}
				</p>
			)}
		</section>
	)
}
